import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  Filter,
  Lightbulb,
  Sparkles,
  Bug,
  ChevronRight,
  MessageSquare,
} from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { PageTransition } from '../../components/animations/PageTransition';
import { REQUEST_STATUSES, REQUEST_TYPES, REQUEST_PRIORITIES } from '@yoda/shared';

interface RequestListItem {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  type: 'idea' | 'feature' | 'bug';
  status: string;
  priority: string | null;
  creatorName: string;
  creatorAvatar: string | null;
  commentCount: number;
  tags: { id: string; name: string; color: string }[];
  createdAt: string;
}

const typeIcons = {
  idea: Lightbulb,
  feature: Sparkles,
  bug: Bug,
};

export function RequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState<RequestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });

  useEffect(() => {
    fetchRequests();
  }, [statusFilter, typeFilter, pagination.page]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(pagination.page));
      params.set('pageSize', '20');
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('type', typeFilter);

      const response = await api.get<RequestListItem[]>(
        `/requests?${params.toString()}`
      ) as any;
      setRequests(response.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 1,
      }));
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = requests.filter(
    (r) =>
      r.title.toLowerCase().includes(search.toLowerCase()) ||
      r.projectName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <PageTransition>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Requests</h1>
            <p className="text-text-secondary">
              Manage client requests and feature ideas
            </p>
          </div>
          <Link to="/requests/new">
            <Button>
              <Plus className="w-4 h-4" />
              New Request
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search requests..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setSearchParams((params) => {
                if (e.target.value) params.set('status', e.target.value);
                else params.delete('status');
                return params;
              });
            }}
            className="px-4 py-2 bg-bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent/50"
          >
            <option value="">All Statuses</option>
            {Object.entries(REQUEST_STATUSES).map(([key, value]) => (
              <option key={key} value={key}>
                {value.label}
              </option>
            ))}
          </select>

          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setSearchParams((params) => {
                if (e.target.value) params.set('type', e.target.value);
                else params.delete('type');
                return params;
              });
            }}
            className="px-4 py-2 bg-bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent/50"
          >
            <option value="">All Types</option>
            {Object.entries(REQUEST_TYPES).map(([key, value]) => (
              <option key={key} value={key}>
                {value.label}
              </option>
            ))}
          </select>
        </div>

        {/* Request List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="bg-bg-secondary rounded-xl p-4 animate-pulse"
              >
                <div className="h-5 w-2/3 bg-bg-tertiary rounded mb-2" />
                <div className="h-4 w-1/3 bg-bg-tertiary rounded" />
              </div>
            ))}
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="bg-bg-secondary rounded-2xl p-12 text-center">
            <Filter className="w-12 h-12 mx-auto text-text-muted mb-4" />
            <h3 className="text-lg font-medium mb-2">No requests found</h3>
            <p className="text-text-secondary mb-6">
              {search || statusFilter || typeFilter
                ? 'Try adjusting your filters'
                : 'Create a new request to get started'}
            </p>
            {!search && !statusFilter && !typeFilter && (
              <Link to="/requests/new">
                <Button>
                  <Plus className="w-4 h-4" />
                  Create Request
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRequests.map((request, index) => {
              const TypeIcon = typeIcons[request.type];
              const typeInfo = REQUEST_TYPES[request.type];
              const statusInfo = REQUEST_STATUSES[request.status as keyof typeof REQUEST_STATUSES];
              const priorityInfo = request.priority
                ? REQUEST_PRIORITIES[request.priority as keyof typeof REQUEST_PRIORITIES]
                : null;

              return (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={`/requests/${request.id}`}>
                    <div className="bg-bg-secondary rounded-xl p-4 hover:bg-bg-tertiary transition-colors group">
                      <div className="flex items-start gap-4">
                        {/* Type Icon */}
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: typeInfo.bgColor }}
                        >
                          <TypeIcon
                            className="w-5 h-5"
                            style={{ color: typeInfo.color }}
                          />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium truncate">{request.title}</h3>
                            {priorityInfo && (
                              <span
                                className="px-2 py-0.5 text-xs rounded-full"
                                style={{
                                  backgroundColor: priorityInfo.bgColor,
                                  color: priorityInfo.color,
                                }}
                              >
                                {priorityInfo.label}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-3 text-sm text-text-secondary">
                            <span>{request.projectName}</span>
                            <span>•</span>
                            <span>{request.creatorName}</span>
                            {request.commentCount > 0 && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="w-3.5 h-3.5" />
                                  {request.commentCount}
                                </span>
                              </>
                            )}
                          </div>

                          {/* Tags */}
                          {request.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {request.tags.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="px-2 py-0.5 text-xs rounded-full"
                                  style={{
                                    backgroundColor: `${tag.color}20`,
                                    color: tag.color,
                                  }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Status & Arrow */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span
                            className="px-3 py-1 text-xs font-medium rounded-full"
                            style={{
                              backgroundColor: statusInfo?.bgColor,
                              color: statusInfo?.color,
                            }}
                          >
                            {statusInfo?.label || request.status}
                          </span>
                          <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-text transition-colors" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-sm text-text-secondary">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page === pagination.totalPages}
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </PageTransition>
  );
}
